import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type HTMLAttributes, type ReactElement, useState } from 'react'
import {
	ResponsiveConfirmDialog,
	ResponsiveConfirmTrigger
} from '@/features/marquee/components/responsive-confirm-dialog'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import { errorHandler } from '@/helpers/utils'
import { startEvent } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

interface StartEventDialogProps {
	/** Event to start via POST /events/{id}/start (locked → live). */
	eventId?: string
	/** Element that opens the confirmation (e.g. the "Start event" button). */
	trigger: ReactElement<HTMLAttributes<HTMLElement>>
}

/**
 * "Start this event?" confirmation. Runs POST /events/{id}/start on confirm,
 * flipping the locked event to live so day-of check-in opens.
 */
function StartEventDialog({ eventId, trigger }: StartEventDialogProps) {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)

	const queryClient = useQueryClient()

	const start = useMutation({
		mutationFn: () => startEvent({ path: { eventId: eventId ?? '' } }),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.marquee.events.detail(eventId ?? '')
			})
			setOpen(false)
		},
		onError: (error) => errorHandler(error)
	})

	return (
		<ResponsiveConfirmDialog
			confirmLabel={t('marquee:start_event')}
			description={t('marquee:start_dialog_description')}
			isPending={start.isPending}
			onConfirm={() => start.mutate()}
			onOpenChange={setOpen}
			open={open}
			title={t('marquee:start_dialog_title')}
		>
			<ResponsiveConfirmTrigger>{trigger}</ResponsiveConfirmTrigger>
		</ResponsiveConfirmDialog>
	)
}

export { StartEventDialog }
